import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { OpsLandmark } from '@popcart/core/v1';
import { api, errorMessage } from '../api';
import { useToast } from '../ui/toast';
import { landmarkToast, mapsKeys, patchLandmarkList, type LandmarkPatch, type LandmarkStatus } from './maphealth/lib.js';

// Ops polling: the board every 5 s, everything else every 30 s (Phase 2; WebSocket later).
export const BOARD_MS = 5_000;
export const SLOW_MS = 30_000;

export type OpsRange = 'today' | '7d' | '30d';

export const opsKeys = {
  overview: (range: OpsRange) => ['ops', 'overview', range] as const,
  board: ['ops', 'board'] as const,
  shops: ['ops', 'shops'] as const,
  riders: ['ops', 'riders'] as const,
  settings: ['ops', 'settings'] as const,
};

export function useOpsOverview(range: OpsRange) {
  return useQuery({ queryKey: opsKeys.overview(range), queryFn: () => api.opsOverview(range), refetchInterval: SLOW_MS });
}
export function useOpsBoard() {
  return useQuery({ queryKey: opsKeys.board, queryFn: () => api.opsBoard(), refetchInterval: BOARD_MS });
}
export function useOpsShops() {
  return useQuery({ queryKey: opsKeys.shops, queryFn: () => api.opsShops(), refetchInterval: SLOW_MS });
}
export function useOpsRiders() {
  return useQuery({ queryKey: opsKeys.riders, queryFn: () => api.opsRiders(), refetchInterval: SLOW_MS });
}
export function useOpsSettings() {
  return useQuery({ queryKey: opsKeys.settings, queryFn: () => api.opsSettings(), staleTime: 60_000 });
}

export type OpsShop = Awaited<ReturnType<typeof api.opsShops>>[number];
export type OpsRider = Awaited<ReturnType<typeof api.opsRiders>>[number];

// ── Map health (native maps: docs/NATIVE-MAPS-CONTRACT.md §3-4) ─────────────────────────────
export function useMapLandmarks(status: LandmarkStatus, enabled = true) {
  return useQuery({ queryKey: mapsKeys.landmarksBy(status), queryFn: () => api.opsMapLandmarks(status), refetchInterval: SLOW_MS, enabled });
}
/** Map gaps are a heavy query over 90 days of fixes and move slowly: every 5 minutes. */
export function useMapGaps() {
  return useQuery({ queryKey: mapsKeys.gaps, queryFn: () => api.opsMapGaps(), refetchInterval: 5 * 60_000 });
}
export function useTrafficCoverage() {
  return useQuery({ queryKey: mapsKeys.coverage, queryFn: () => api.opsTrafficCoverage(), refetchInterval: 60_000 });
}

/**
 * Approve, hide, rename or re-categorise a landmark. Optimistic on every cached status tab (a
 * status change takes it off the tab it left), rolled back if the server says no; every tab
 * refetches once it settles.
 */
export function useUpdateLandmark() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (v: { landmark: OpsLandmark; patch: LandmarkPatch }) => api.updateMapLandmark(v.landmark.id, v.patch),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: mapsKeys.landmarks });
      const prev = qc.getQueriesData<OpsLandmark[]>({ queryKey: mapsKeys.landmarks });
      for (const [key] of prev) {
        const tab = key[3] as LandmarkStatus;
        qc.setQueryData<OpsLandmark[]>(key, (list) => patchLandmarkList(list, tab, v.landmark.id, v.patch));
      }
      return { rollback: () => prev.forEach(([k, d]) => qc.setQueryData(k, d)) };
    },
    onError: (err, _v, ctx) => {
      ctx?.rollback();
      toast(errorMessage(err, "Couldn't save the landmark. Try again."), 'error');
    },
    onSuccess: (saved, v) => toast(landmarkToast(saved?.name ?? v.landmark.name, v.patch)),
    onSettled: () => void qc.invalidateQueries({ queryKey: mapsKeys.landmarks }),
  });
}
